import { z } from 'zod';
import { dateInputSchema, isoDateStringSchema, objectIdSchema } from './common.js';
import { taskPrioritySchema, taskStatusSchema } from './sales-vocabulary.js';

/**
 * What somebody has undertaken to DO about a customer, and when.
 *
 * A follow-up is a task, and nothing else: there is no follow-up field on the customer, and the
 * only place one is made or moved is the customer's Follow-ups tab (or the Follow-ups page).
 *
 * THIS FILE OWNS THE FOLLOW-UP DATE. The customer's next follow-up date is a CACHE — the earliest
 * `dueAt` among the customer's OPEN tasks — recomputed inside the same transaction as every task
 * write and settable by nothing else, so there is ONE answer to "when next", and creating the task
 * IS setting the date. Clearing a follow-up means closing a task, which is the honest version of
 * the same act — a date that nobody undertook to act on was never a plan.
 *
 * A task is the FUTURE. A call is the PAST — see `customer-follow-ups.ts`. Scheduling a call is a
 * task with the "Call Back" category, and that is deliberately the only way to express it.
 *
 * Status and priority are closed enums (`sales-vocabulary.ts`): `open` is the only status the
 * follow-up date is derived from, so a tenant must not be able to invent one the derivation does
 * not recognise. Categories are the shop's own vocabulary and configurable.
 */

export const TASK_NAME_MAX_LENGTH = 120;
export const TASK_DESCRIPTION_MAX_LENGTH = 2000;

/* -------------------------------------------------------------------------------------------
 * Categories — the shop's vocabulary, not the product's
 * ---------------------------------------------------------------------------------------- */

export const MAX_TASK_CATEGORY_LABEL_LENGTH = 40;
/** A cap, not a target. Past this a picker is a scroll rather than a choice. */
export const MAX_TASK_CATEGORIES = 30;

export const taskCategoryKeySchema = z
  .string()
  .trim()
  .min(1)
  .max(40)
  .regex(/^[a-z][a-zA-Z0-9_]*$/, 'A task category key is lower snake_case');

export const taskCategoryLabelSchema = z
  .string()
  .trim()
  .min(1, 'Give the category a name')
  .max(MAX_TASK_CATEGORY_LABEL_LENGTH)
  .transform((value) => value.replace(/\s+/g, ' '));

/**
 * One row of a shop's category list, both as stored and as served.
 *
 * The storage rules follow `craft-roles.ts`: the KEY is derived once from the label the shop
 * first typed and is then immutable, so a category renamed after two years still matches the
 * tasks pointing at it. Renaming writes `label`; retiring writes `active: false`, which removes it
 * from every picker while still resolving a label for tasks that already carry it.
 */
export const taskCategorySchema = z.object({
  key: taskCategoryKeySchema,
  label: z.string(),
  active: z.boolean(),
  order: z.number().int(),
});
export type TaskCategory = z.infer<typeof taskCategorySchema>;

export const taskCategoryListSchema = z.object({
  categories: z.array(taskCategorySchema),
});
export type TaskCategoryList = z.infer<typeof taskCategoryListSchema>;

/**
 * What every shop starts with, and what a shop whose settings predate this feature reads as.
 *
 * Keys are written out rather than derived, so a shop seeded at signup and a shop that never
 * stored a row read identically — which is what makes this safe to ship with no backfill.
 */
export const DEFAULT_TASK_CATEGORIES: readonly { key: string; label: string }[] = Object.freeze([
  { key: 'follow_up', label: 'Follow Up' },
  { key: 'call_back', label: 'Call Back' },
  { key: 'send_quote', label: 'Send Quote' },
  { key: 'trial_fitting', label: 'Trial Fitting' },
  { key: 'delivery_reminder', label: 'Delivery Reminder' },
]);

/** Adding one. The KEY is absent on purpose — the server derives it from the label, once. */
export const addTaskCategorySchema = z.object({ label: taskCategoryLabelSchema });
export type AddTaskCategoryInput = z.input<typeof addTaskCategorySchema>;

/** Editing one. `key` is not accepted, so a rename can only ever be a label. */
export const updateTaskCategorySchema = z
  .object({
    label: taskCategoryLabelSchema.optional(),
    active: z.boolean().optional(),
  })
  .refine((patch) => patch.label !== undefined || patch.active !== undefined, {
    message: 'Nothing to update',
  });
export type UpdateTaskCategoryInput = z.input<typeof updateTaskCategorySchema>;

export const reorderTaskCategoriesSchema = z.object({
  order: z.array(taskCategoryKeySchema).min(1),
});
export type ReorderTaskCategoriesInput = z.input<typeof reorderTaskCategoriesSchema>;

/* -------------------------------------------------------------------------------------------
 * Requests
 * ---------------------------------------------------------------------------------------- */

/**
 * Making one.
 *
 * `dueAt` is REQUIRED, and that is the difference between this and a wish: a task with no date
 * contributes nothing to the follow-up derivation, so it would be invisible to every screen that
 * exists to surface work.
 *
 * `ownerUserId` omitted means the customer's own assignee, resolved by the server. A client must
 * not guess it: the browser's copy of the customer can be minutes stale.
 */
export const createTaskSchema = z.object({
  name: z.string().trim().min(1, 'Name the task').max(TASK_NAME_MAX_LENGTH),
  /** A key from the shop's own list, or null for a task that fits none of them. */
  categoryKey: taskCategoryKeySchema.nullish(),
  dueAt: dateInputSchema,
  priority: taskPrioritySchema.default('normal'),
  /** Defaults to the customer's assignee, server-side. */
  ownerUserId: objectIdSchema.nullish(),
  description: z.string().trim().max(TASK_DESCRIPTION_MAX_LENGTH).nullish(),
});
export type CreateTaskInput = z.input<typeof createTaskSchema>;

/**
 * Editing one. Status is NOT here — it moves through its own endpoint, because closing a task
 * recomputes the follow-up date and may log a call, and that is a different act from correcting
 * a typo in the name.
 */
export const updateTaskSchema = z
  .object({
    name: z.string().trim().min(1).max(TASK_NAME_MAX_LENGTH).optional(),
    categoryKey: taskCategoryKeySchema.nullish(),
    dueAt: dateInputSchema.optional(),
    priority: taskPrioritySchema.optional(),
    ownerUserId: objectIdSchema.nullish(),
    description: z.string().trim().max(TASK_DESCRIPTION_MAX_LENGTH).nullish(),
  })
  .refine((patch) => Object.values(patch).some((value) => value !== undefined), {
    message: 'Nothing to update',
  });
export type UpdateTaskInput = z.input<typeof updateTaskSchema>;

/** Closing one, or putting it back. Every one of these recomputes the follow-up date. */
export const setTaskStatusSchema = z.object({ status: taskStatusSchema });
export type SetTaskStatusInput = z.input<typeof setTaskStatusSchema>;

/* -------------------------------------------------------------------------------------------
 * Views
 * ---------------------------------------------------------------------------------------- */

/** Who a task is about. A customer, always — it is the only contact record. */
export const TASK_SUBJECT_KINDS = ['customer'] as const;
export type TaskSubjectKind = (typeof TASK_SUBJECT_KINDS)[number];

export const taskSchema = z.object({
  id: objectIdSchema,
  customerId: objectIdSchema.nullable(),
  name: z.string(),
  categoryKey: z.string().nullable(),
  /** Resolved from the shop's list at read time, so a rename shows everywhere at once. */
  categoryLabel: z.string().nullable(),
  dueAt: isoDateStringSchema,
  status: taskStatusSchema,
  priority: taskPrioritySchema,
  ownerUserId: z.string().nullable(),
  ownerName: z.string().nullable(),
  description: z.string().nullable(),
  /** Stamped when the task left `open`. Null while it is still open. */
  closedAt: isoDateStringSchema.nullable(),
  createdByUserId: z.string().nullable(),
  createdByName: z.string().nullable(),
  createdAt: isoDateStringSchema,
});
export type Task = z.infer<typeof taskSchema>;

export const taskListSchema = z.object({ items: z.array(taskSchema) });
export type TaskList = z.infer<typeof taskListSchema>;

/* -------------------------------------------------------------------------------------------
 * "My follow-ups"
 * ---------------------------------------------------------------------------------------- */

/**
 * One row of the queue that answers "what am I doing today".
 *
 * It exists because a task's owner and a customer's assignee are allowed to differ, so the
 * customer list — which filters by assignee — cannot answer it.
 *
 * The person's name and number ride along denormalised: this screen is read to start a phone
 * call, and a queue that needs a second request per row to show who to ring is a queue that
 * renders empty for a second on a shop's connection.
 */
export const followUpQueueItemSchema = taskSchema.extend({
  subjectKind: z.enum(TASK_SUBJECT_KINDS),
  subjectName: z.string(),
  subjectPhone: z.string(),
});
export type FollowUpQueueItem = z.infer<typeof followUpQueueItemSchema>;

export const FOLLOW_UP_QUEUE_SCOPES = ['mine', 'all'] as const;
export type FollowUpQueueScope = (typeof FOLLOW_UP_QUEUE_SCOPES)[number];

export const followUpQueueQuerySchema = z.object({
  /** `all` is for an owner reviewing the shop; `mine` is the default a rep opens on. */
  scope: z.enum(FOLLOW_UP_QUEUE_SCOPES).default('mine'),
  /** Only tasks due before this instant. Omitted means everything still open. */
  dueBefore: dateInputSchema.optional(),
});
export type FollowUpQueueQuery = z.infer<typeof followUpQueueQuerySchema>;

export const followUpQueueSchema = z.object({
  items: z.array(followUpQueueItemSchema),
  overdue: z.number().int(),
  dueToday: z.number().int(),
});
export type FollowUpQueue = z.infer<typeof followUpQueueSchema>;
